import { Icon, Text, Tooltip } from '@scality/core-ui';
// core-ui's own button shell: `Button` forwards no ref, and the menu must anchor to the element.
import { ButtonStyled } from '@scality/core-ui/dist/components/buttonv2/Buttonv2.component';
import { useSelect } from 'downshift';
import styled from 'styled-components';
import {
  mergeRefs,
  SelectMenuPanel,
  SelectMenuPlainList,
  SelectMenuRow,
  SelectMenuScroll,
  SelectWrapper,
  useFloatingSelectMenu,
} from '../select';
import { downloadExportTable, type ExportDataFormat, type ExportTable } from './exportTable';

const TOOLTIP = 'Export data';

const FORMATS: { format: ExportDataFormat; label: string }[] = [
  { format: 'csv', label: 'CSV' },
  { format: 'json', label: 'JSON' },
];

/** Explicit width, never a percentage: the panel is `fixed`, so `100%` is the whole viewport. */
const Menu = styled(SelectMenuPanel)`
  min-width: 7rem;
  width: max-content;
`;

type WidgetExportMenuProps = {
  /** Accessible name; it names the widget the data comes from. */
  label: string;
  /** Read when a format is picked, so nothing is flattened until a file is actually asked for. */
  buildTable: () => ExportTable;
};

/** The widget's data as a CSV or JSON file, on the same pieces as `DashboardExportMenu`. */
export const WidgetExportMenu = ({ label, buildTable }: WidgetExportMenuProps) => {
  const { isOpen, getToggleButtonProps, getMenuProps, getItemProps, highlightedIndex } = useSelect({
    items: FORMATS,
    itemToString: (item) => item?.label ?? '',
    // Held at null so the same format can be picked twice: exporting is an action, not a choice.
    selectedItem: null,
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) downloadExportTable(buildTable(), selectedItem.format);
    },
  });

  const { refs: floatingRefs, floatingStyles } = useFloatingSelectMenu({
    isOpen,
    preferredBelow: true,
    mainAxisAbovePx: 8,
    mainAxisBelowPx: 8,
  });

  const { ref: menuRef, style: menuStyle, ...menuRest } = getMenuProps();
  // A combobox takes no name from its contents, and downshift points it at a label we have not got.
  const { ref: toggleRef, ...toggleRest } = getToggleButtonProps({
    'aria-label': label,
    'aria-labelledby': undefined,
  });

  return (
    <SelectWrapper>
      <Tooltip overlay={TOOLTIP}>
        <ButtonStyled
          type="button"
          ref={mergeRefs(floatingRefs.setReference, toggleRef)}
          {...toggleRest}
          $variant="outline"
          $size="inline"
        >
          <Icon name="Export" />
        </ButtonStyled>
      </Tooltip>
      <Menu
        ref={mergeRefs(floatingRefs.setFloating, menuRef)}
        {...menuRest}
        style={{ ...menuStyle, ...floatingStyles }}
        aria-hidden={!isOpen}
        $isOpen={isOpen}
      >
        <SelectMenuScroll>
          <SelectMenuPlainList>
            {isOpen &&
              FORMATS.map((item, index) => (
                <SelectMenuRow
                  key={item.format}
                  $highlighted={highlightedIndex === index}
                  {...getItemProps({ item, index })}
                >
                  <Text>{item.label}</Text>
                </SelectMenuRow>
              ))}
          </SelectMenuPlainList>
        </SelectMenuScroll>
      </Menu>
    </SelectWrapper>
  );
};
